/**
 * CornerAssistant - Floating AUI assistant button
 *
 * A discrete "?" button in the lower right corner that opens
 * the AUI chat interface. Uses the LLM agent endpoint for
 * conversational interaction with tools (search, buffers, BQL).
 * Falls back to search-only mode if the agent is unavailable.
 *
 * Features:
 * - Draggable by header (double-click to reset position)
 * - Minimizable to title bar
 * - Basic markdown rendering for agent responses
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useBufferSync } from '../contexts/BufferSyncContext';
import { usePanels } from '../contexts/PanelContext';
import type { SearchResult, AuiChatResponse } from '../contexts/ApiContext';
import { createLogger } from '../utils/debug';

const log = createLogger('CornerAssistant');

interface CornerAssistantProps {
  onSelectResult?: (result: SearchResult) => void;
  sessionId?: string;
  isConnected?: boolean;
  /** Current workspace view mode (from MainWorkspace) */
  viewMode?: string;
}

interface AgenticStep {
  type: string;
  content: string;
  tool?: string;
  durationMs?: number;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  results?: SearchResult[];
  agentSteps?: AgenticStep[];
  responseType?: AuiChatResponse['type'];
}

/** Lightweight markdown to JSX for assistant messages */
function renderMarkdown(text: string): React.ReactNode {
  // Split into paragraphs on double newline
  const paragraphs = text.split(/\n\n+/);

  return (
    <div className="corner-assistant__markdown">
      {paragraphs.map((para, i) => {
        const trimmed = para.trim();
        if (!trimmed) return null;

        // Check if it's a list (lines starting with - or * or 1.)
        const lines = trimmed.split('\n');
        const isBulletList = lines.every((l) => /^\s*[-*]\s/.test(l));
        const isNumberedList = lines.every((l) => /^\s*\d+[.)]\s/.test(l));

        if (isBulletList) {
          return (
            <ul key={i}>
              {lines.map((line, j) => (
                <li key={j}>{renderInline(line.replace(/^\s*[-*]\s/, ''))}</li>
              ))}
            </ul>
          );
        }
        if (isNumberedList) {
          return (
            <ol key={i}>
              {lines.map((line, j) => (
                <li key={j}>{renderInline(line.replace(/^\s*\d+[.)]\s/, ''))}</li>
              ))}
            </ol>
          );
        }

        // Regular paragraph (join soft line breaks)
        return <p key={i}>{renderInline(lines.join(' '))}</p>;
      })}
    </div>
  );
}

/** Render inline markdown: **bold**, *italic*, `code` */
function renderInline(text: string): React.ReactNode {
  // Split on inline patterns
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Match **bold**, *italic*, or `code`
    const match = remaining.match(/(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/) ;
    if (!match || match.index === undefined) {
      parts.push(remaining);
      break;
    }

    // Text before match
    if (match.index > 0) {
      parts.push(remaining.slice(0, match.index));
    }

    if (match[2]) {
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={key++}>{match[3]}</em>);
    } else if (match[4]) {
      parts.push(<code key={key++}>{match[4]}</code>);
    }

    remaining = remaining.slice(match.index + match[0].length);
  }

  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>;
}

// ═══════════════════════════════════════════════════════════════════════════
// AGENT STEPS DISPLAY — collapsible trace of tool usage
// ═══════════════════════════════════════════════════════════════════════════

function AgentStepsDisplay({ steps }: { steps: AgenticStep[] }) {
  const [expanded, setExpanded] = useState(false);
  const toolSteps = steps.filter(s => s.tool);
  if (toolSteps.length === 0) return null;

  return (
    <div className="corner-assistant__agent-steps">
      <button
        className="corner-assistant__agent-steps-toggle"
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
      >
        <span className="corner-assistant__agent-steps-icon">{expanded ? '\u25BE' : '\u25B8'}</span>
        <span>{toolSteps.length} tool{toolSteps.length !== 1 ? 's' : ''} used</span>
      </button>
      {expanded && (
        <ul className="corner-assistant__agent-steps-list">
          {toolSteps.map((step, i) => (
            <li key={i} className="corner-assistant__agent-step">
              <code className="corner-assistant__agent-step-tool">{step.tool}</code>
              {step.durationMs != null && (
                <span className="corner-assistant__agent-step-time">
                  {step.durationMs < 1000
                    ? `${step.durationMs}ms`
                    : `${(step.durationMs / 1000).toFixed(1)}s`}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LOCAL UI HELP — answers common questions without cloud API
// ═══════════════════════════════════════════════════════════════════════════

interface HelpEntry {
  patterns: RegExp[];
  answer: string;
}

const UI_HELP: HelpEntry[] = [
  {
    patterns: [/draft.*grey|draft.*gray|draft.*disabled|draft.*greyed|enable.*draft|create.*draft|make.*draft|how.*draft/i],
    answer: `**To create a draft:**

1. Switch to **Stack** view and add 2+ cards from your archive
2. Click the **Outline** button in the toolbar
3. Choose an arc type (or use "Auto") and click **Generate Outline**
4. Edit the outline sections as needed
5. Scroll to the bottom and click **Generate Draft**

The Draft button in the toolbar activates only *after* you generate a draft through the outline. It's there to switch back to your draft after viewing other content.`,
  },
  {
    patterns: [/how.*outline|create.*outline|generate.*outline|what.*outline/i],
    answer: `**To create an outline:**

1. Switch to **Stack** view in the toolbar
2. Add at least 2 cards from your archive (use Browse, Search, or the Curator)
3. Click the **Outline** button — it activates when you have 2+ cards
4. Choose an arc type: *Narrative*, *Thematic*, *Chronological*, *Exploratory*, or let **Auto** detect the best fit
5. Click **Generate Outline**
6. Edit section titles and notes, then click **Generate Draft** at the bottom`,
  },
  {
    patterns: [/how.*stack|add.*card|add.*stack|what.*stack/i],
    answer: `**Using the Stack:**

The Stack is your card collector. Switch to Stack view in the toolbar.

**Ways to add cards:**
- In the Archive panel, browse or search for content, then click **Add to Stack**
- Use the **Curator** (Find in Archive) to search while writing
- From search results, click a result to add it

**Working with cards:**
- Reorder cards by dragging
- Edit card content directly
- Remove cards with the X button
- When you have 2+ cards, the **Outline** button activates`,
  },
  {
    patterns: [/keyboard|shortcut|hotkey|keybind/i],
    answer: `**Keyboard Shortcuts:**

- **Cmd+E** — Cycle view modes (Read → Edit → Split → Stack → Outline → Draft)
- **Cmd+/** — Toggle this AUI assistant
- **Cmd+[** — Toggle Archive panel (left)
- **Cmd+]** — Toggle Tools panel (right)
- **Cmd+F** — Find in Archive (Curator)
- **Arrow keys** — Navigate messages in thread mode
- **Escape** — Close modals and overlays`,
  },
  {
    patterns: [/how.*use|getting.*start|tutorial|guide|help.*using|where.*start|new.*here/i],
    answer: `**Quick Start Guide:**

**1. Browse your archive** — Use the Archive panel (left) to browse, search, or import conversations.

**2. Collect cards in the Stack** — Switch to Stack view. Add passages from your archive as cards for synthesis.

**3. Generate an Outline** — With 2+ cards, click Outline. Choose an arc type or let AI auto-detect. Click Generate Outline.

**4. Create a Draft** — Edit outline sections, then scroll down and click Generate Draft. The workspace switches to Draft view.

**5. Refine** — Edit your draft freely. Select text for AI-assisted rewrites. Use "View with Sources" to see provenance.

**Tip:** Use Cmd+E to cycle through view modes anytime.`,
  },
  {
    patterns: [/what.*curator|how.*curator|find.*archive|search.*while.*writ/i],
    answer: `**The Curator (Find in Archive):**

The Curator lets you search your archive while writing. Open it with **Cmd+F** or the toolbar button.

- Enter a search query or use an anchor passage for semantic similarity
- Results can be **added to your stack** directly
- Saved searches persist per project in the left sidebar
- Click a saved search to reload it later`,
  },
  {
    patterns: [/what.*panel|layout|three.*panel|where.*tool|where.*archive/i],
    answer: `**Studio Layout:**

The studio has 3 panels:

- **Archive** (left, Cmd+[) — Browse, search, import, curate conversations
- **Workspace** (center) — Read, edit, stack, outline, and draft content
- **Tools** (right, Cmd+]) — AI analysis (sentencing, AI detection) and transformations

Toggle panels with their keyboard shortcuts to focus on what matters.`,
  },
  {
    patterns: [/view.*mode|read.*edit|split.*mode|what.*mode/i],
    answer: `**Workspace View Modes** (cycle with Cmd+E):

1. **Read** — Rendered markdown view
2. **Edit** — Side-by-side editor with live preview
3. **Split** — Before/after comparison (original vs transformed)
4. **Stack** — Card collector for gathering source material
5. **Outline** — Generate structured outlines from stacked cards
6. **Draft** — Edit and refine your generated draft`,
  },
];

/** Try to match a user message to a local UI help entry */
function matchLocalHelp(message: string): string | null {
  const normalized = message.toLowerCase().trim();
  for (const entry of UI_HELP) {
    for (const pattern of entry.patterns) {
      if (pattern.test(normalized)) {
        return entry.answer;
      }
    }
  }
  return null;
}

export function CornerAssistant({ onSelectResult, sessionId, isConnected = false, viewMode }: CornerAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState<'agent' | 'search'>('agent');

  // Drag state
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
  const isDragging = useRef(false);
  const dragStart = useRef<{ mouseX: number; mouseY: number; elX: number; elY: number } | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);

  const api = useApi();
  const {
    workingContent, activeBufferName, getTextContent, importArchiveNode,
    focusedText, selectedText, threadIndex, activeStackIndex, stackItems,
  } = useBufferSync();
  const { setArchiveSearch } = usePanels();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Compute workspace context for AUI system prompt
  const workspaceContext = useMemo(() => {
    if (workingContent.length === 0) {
      return null;
    }
    const totalWords = workingContent.reduce(
      (sum, item) => sum + (item.metadata.wordCount ?? 0),
      0
    );
    const preview = workingContent
      .slice(0, 2)
      .map((item) => item.text.substring(0, 50) + (item.text.length > 50 ? '...' : ''))
      .join(' | ');

    return {
      bufferName: activeBufferName,
      itemCount: workingContent.length,
      wordCount: totalWords,
      preview,
      viewMode: viewMode || 'read',
    };
  }, [workingContent, activeBufferName, viewMode]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Keyboard shortcut: Cmd+/ to toggle chat
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
      // Escape to close chat
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Drag handlers
  const handleHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    // Don't drag if clicking a button
    if ((e.target as HTMLElement).closest('button')) return;
    // Don't drag on mobile
    if (window.innerWidth <= 480) return;

    e.preventDefault();
    isDragging.current = true;

    const el = chatRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      elX: rect.left,
      elY: rect.top,
    };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current || !dragStart.current) return;
      const dx = ev.clientX - dragStart.current.mouseX;
      const dy = ev.clientY - dragStart.current.mouseY;
      setDragPosition({
        x: dragStart.current.elX + dx,
        y: dragStart.current.elY + dy,
      });
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      dragStart.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Double-click header to reset position
  const handleHeaderDoubleClick = useCallback(() => {
    setDragPosition(null);
  }, []);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(Math.max(textarea.scrollHeight, 40), 120);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  // Build conversation history from messages state
  const buildHistory = useCallback(() => {
    return messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));
  }, [messages]);

  // Parse search results from agent response data
  const extractResults = (data: unknown): SearchResult[] => {
    if (!data) return [];
    if (Array.isArray(data)) {
      return data.filter(
        (item): item is SearchResult =>
          item && typeof item === 'object' && 'id' in item && 'text' in item
      );
    }
    return [];
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    if (inputRef.current) {
      inputRef.current.style.height = '40px';
    }

    // Try local UI help first (works offline, instant response)
    const localHelp = matchLocalHelp(input.trim());
    if (localHelp) {
      const helpMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: localHelp,
      };
      setMessages((prev) => [...prev, helpMessage]);
      setIsLoading(false);
      return;
    }

    // Electron: route through local IPC (uses AUI model role)
    if (window.electronAui) {
      try {
        const history = buildHistory();
        // Gather workspace context — prefer focused message over full buffer
        const context: {
          activeBufferName?: string;
          activeText?: string;
          selectedText?: string;
          viewMode?: string;
          position?: { mode: string; current: number; total: number };
        } = {};
        if (activeBufferName) context.activeBufferName = activeBufferName;
        if (viewMode) context.viewMode = viewMode;
        // Use focused text (current message) when available, else full buffer truncated
        context.activeText = focusedText || getTextContent?.()?.slice(0, 3000) || undefined;
        if (selectedText) context.selectedText = selectedText;
        // Position metadata for thread/stack navigation
        if (threadIndex) {
          context.position = { mode: 'thread', current: threadIndex.currentIndex + 1, total: workingContent.length };
        } else if (activeStackIndex != null && stackItems.length > 0) {
          context.position = { mode: 'stack', current: activeStackIndex + 1, total: stackItems.length };
        }

        // Use agentic mode for action-oriented requests, simple chat otherwise
        const isActionRequest = /\b(search|find|create|make|list|analyze|humanize|rewrite|transform|show me|look up|get|browse)\b/i.test(input.trim());
        const useAgentic = isActionRequest && !!window.electronAui.chatAgentic;

        if (useAgentic) {
          const response = await window.electronAui.chatAgentic(input.trim(), history, context);

          const assistantMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: response.message,
            agentSteps: response.steps.length > 0 ? response.steps : undefined,
            responseType: 'success',
          };

          setMessages((prev) => [...prev, assistantMessage]);
        } else {
          const response = await window.electronAui.chat(input.trim(), history, context);

          const assistantMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: response.message,
            responseType: 'success',
          };

          setMessages((prev) => [...prev, assistantMessage]);
        }
      } catch (err: unknown) {
        const detail = err instanceof Error ? err.message : 'Chat failed. Please try again.';
        const errorMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: detail,
        };
        setMessages((prev) => [...prev, errorMessage]);
        log.error('AUI chat failed:', err);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Cloud: check if session is available
    if (!sessionId) {
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'API not connected. I can still answer questions about the interface — try asking "how do I create a draft?" or "keyboard shortcuts".',
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsLoading(false);
      return;
    }

    // Try agent chat first, fall back to search
    if (chatMode === 'agent') {
      try {
        const history = buildHistory();
        const response = await api.chat(sessionId, input.trim(), history);

        const results = extractResults(response.data);

        // Route search results to archive panel for proper rendering
        if (results.length > 0) {
          setArchiveSearch(input.trim(), results);
        }

        const assistantMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: results.length > 0
            ? `Found ${results.length} result${results.length !== 1 ? 's' : ''} — see archive panel.${response.message ? '\n\n' + response.message : ''}`
            : response.message,
          responseType: response.type,
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err: unknown) {
        // If agent endpoint fails (503/404/500), fall back to search mode
        const httpStatus =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { status?: number } }).response?.status
            : undefined;
        const isUnavailable = httpStatus === 503 || httpStatus === 404 || httpStatus === 500;

        // Detect timeout (ky throws TimeoutError)
        const isTimeout =
          err instanceof Error && (err.name === 'TimeoutError' || err.message.includes('timeout'));

        if (isUnavailable) {
          setChatMode('search');
          // Retry as search
          await handleSearchFallback(sessionId, input.trim());
        } else {
          const detail = isTimeout
            ? 'Request timed out. The agent may need more time — try a simpler query.'
            : err instanceof Error
              ? err.message
              : 'Chat failed. Please try again.';
          const errorMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: 'assistant',
            content: detail,
          };
          setMessages((prev) => [...prev, errorMessage]);
          log.error('Chat failed:', err);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      await handleSearchFallback(sessionId, input.trim());
      setIsLoading(false);
    }
  };

  const handleSearchFallback = async (sid: string, query: string) => {
    try {
      const response = await api.search(sid, query, { limit: 5 });

      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content:
          response.results.length > 0
            ? `Found ${response.results.length} results:`
            : 'No results found. Try a different query.',
        results: response.results,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: 'Search failed. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
      log.error('Search failed:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    adjustTextareaHeight();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessages([]);
    setChatMode('agent'); // Reset to agent mode on clear
  };

  // Handle result click - imports to buffer and notifies parent
  const handleResultClick = useCallback(async (result: SearchResult) => {
    // Import to buffer via BufferSyncContext
    await importArchiveNode({
      id: result.id,
      text: result.text,
      type: 'search-result',
      sourceType: result.provenance?.sourceType || result.source,
      threadId: result.provenance?.threadRootId,
      threadTitle: result.provenance?.threadTitle,
      authorRole: result.provenance?.authorRole,
      wordCount: result.wordCount,
    });

    // Also notify parent for backward compatibility
    onSelectResult?.(result);
  }, [importArchiveNode, onSelectResult]);

  // Compute style for dragged position
  const chatStyle: React.CSSProperties | undefined = dragPosition
    ? { left: dragPosition.x, top: dragPosition.y }
    : undefined;

  const chatClassName = [
    'corner-assistant__chat',
    dragPosition ? 'corner-assistant__chat--dragged' : '',
    isMinimized ? 'corner-assistant__chat--minimized' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {/* The floating button */}
      <button
        className={`corner-assistant ${isOpen ? 'corner-assistant--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="AUI Assistant (Cmd+/)"
        aria-label="Open assistant"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <span className="corner-assistant__icon">{isOpen ? '\u00d7' : '?'}</span>
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div
          ref={chatRef}
          className={chatClassName}
          style={chatStyle}
          role="dialog"
          aria-modal="false"
          aria-label="AUI Assistant"
        >
          {/* Header - draggable */}
          <div
            className="corner-assistant__chat-header"
            onMouseDown={handleHeaderMouseDown}
            onDoubleClick={handleHeaderDoubleClick}
          >
            <div className="corner-assistant__chat-title">
              <span className="corner-assistant__chat-icon">&#10022;</span>
              <span>AUI Assistant</span>
              {chatMode === 'search' && (
                <span className="corner-assistant__mode-badge">search only</span>
              )}
            </div>
            <div className="corner-assistant__chat-actions">
              <button
                className="corner-assistant__chat-minimize"
                onClick={() => setIsMinimized((prev) => !prev)}
                title={isMinimized ? 'Expand' : 'Minimize'}
                aria-label={isMinimized ? 'Expand' : 'Minimize'}
              >
                {isMinimized ? '\u25FB' : '\u2014'}
              </button>
              <button
                className="corner-assistant__chat-action"
                onClick={handleClear}
                title="Clear conversation"
                aria-label="Clear conversation"
              >
                &#8634;
              </button>
              <button
                className="corner-assistant__chat-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close (Escape)"
              >
                &#215;
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            className="corner-assistant__chat-messages"
            role="log"
            aria-live="polite"
            aria-label="Conversation messages"
          >
            {messages.length === 0 && (
              <div className="corner-assistant__chat-welcome">
                <p>I can help you search your archive, create drafts, and navigate the studio.</p>
                <p className="corner-assistant__chat-hint">
                  Try: "how do I create a draft?" or "keyboard shortcuts" or "find conversations about music"
                </p>
                {/* Workspace context indicator */}
                {workspaceContext && (
                  <div className="corner-assistant__workspace-context">
                    <span className="corner-assistant__workspace-label">Workspace:</span>
                    <span className="corner-assistant__workspace-info">
                      {workspaceContext.itemCount} item{workspaceContext.itemCount !== 1 ? 's' : ''} • {workspaceContext.wordCount} words
                    </span>
                    <span className="corner-assistant__workspace-preview">
                      {workspaceContext.preview}
                    </span>
                  </div>
                )}
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`corner-assistant__chat-msg corner-assistant__chat-msg--${msg.role}`}
              >
                {msg.role === 'assistant' ? renderMarkdown(msg.content) : <p>{msg.content}</p>}
                {msg.agentSteps && msg.agentSteps.length > 0 && (
                  <AgentStepsDisplay steps={msg.agentSteps} />
                )}
                {msg.results && msg.results.length > 0 && (
                  <div className="corner-assistant__results">
                    {msg.results.map((result, idx) => (
                      <button
                        key={result.id}
                        className="corner-assistant__result-card"
                        onClick={() => handleResultClick(result)}
                      >
                        <div className="corner-assistant__result-header">
                          <span className="corner-assistant__result-index">{idx + 1}</span>
                          <span className="corner-assistant__result-source">
                            {result.provenance?.threadTitle || result.source}
                          </span>
                          <span className="corner-assistant__result-level">
                            L{result.hierarchyLevel}
                          </span>
                        </div>
                        <div className="corner-assistant__result-preview">
                          {result.text.slice(0, 150)}
                          {result.text.length > 150 ? '...' : ''}
                        </div>
                        <div className="corner-assistant__result-footer">
                          <div className="corner-assistant__result-ratings">
                            <span className="corner-assistant__result-rating">
                              {Math.round(result.score * 100)}%
                            </span>
                            {result.quality && (
                              <span className="corner-assistant__result-rating">
                                Q: {Math.round(result.quality.qualityScore * 100)}
                              </span>
                            )}
                          </div>
                          <span className="corner-assistant__result-meta">
                            {result.wordCount} words
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="corner-assistant__chat-msg corner-assistant__chat-msg--loading">
                <div className="corner-assistant__loading-dots">
                  <span>&#9679;</span>
                  <span>&#9679;</span>
                  <span>&#9679;</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="corner-assistant__chat-input">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={isConnected ? "Ask anything about your archive..." : "Connecting to API..."}
              disabled={isLoading}
              rows={1}
              aria-label="Chat input"
            />
            <button
              className="corner-assistant__chat-send"
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              aria-label="Send"
            >
              &#8593;
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default CornerAssistant;
